/** カタログ(SaaS)と依頼先(協力会社)のマスタ。読み取りは core、書き込みは hr:admin。 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Flex,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";

import type { CatalogItem, CatalogItemInput, Partner, PartnerInput } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { CONNECTORS, FULFILLMENTS } from "./constants";
import { errorText } from "./hooks";
import { useCatalog, useInvalidateWf, usePartners, useWfPerms } from "./wfHooks";

export function CatalogPage() {
  const api = useApi();
  const invalidate = useInvalidateWf();
  const { canAdmin } = useWfPerms();
  const catalog = useCatalog();
  const partners = usePartners();

  const [item, setItem] = useState<(CatalogItemInput & { id?: number }) | null>(null);
  const [partner, setPartner] = useState<(PartnerInput & { id?: number }) | null>(null);

  const saveItem = useMutation({
    mutationFn: () => {
      const { id, ...body } = item!;
      return id ? api.updateCatalogItem(id, body) : api.createCatalogItem(body);
    },
    onSuccess: () => {
      void message.success("カタログ項目を保存した");
      setItem(null);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const savePartner = useMutation({
    mutationFn: () => {
      const { id, ...body } = partner!;
      return id ? api.updatePartner(id, body) : api.createPartner(body);
    },
    onSuccess: () => {
      void message.success("依頼先を保存した");
      setPartner(null);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const del = (fn: () => Promise<unknown>, ok: string) =>
    fn().then(
      () => {
        void message.success(ok);
        invalidate();
      },
      (e: unknown) => void message.error(errorText(e)),
    );

  const itemColumns: ColumnsType<CatalogItem> = [
    { title: "SaaS", width: 160, dataIndex: "name" },
    {
      title: "発行方式",
      width: 360,
      render: (_: unknown, r: CatalogItem) => (
        <Space size={4}>
          <span>{r.fulfillment_label}</span>
          {r.fulfillment === "auto" && r.connector && <Tag>{r.connector}</Tag>}
          {r.fulfillment === "manual" && r.default_owner && <Tag>{r.default_owner}</Tag>}
          {r.fulfillment === "external" && r.partner_name && <Tag>{r.partner_name}</Tag>}
        </Space>
      ),
    },
    { title: "備考", width: 320, dataIndex: "note", ellipsis: true },
    {
      title: "状態",
      width: 110,
      render: (_: unknown, r: CatalogItem) =>
        r.active ? <Tag color="green">有効</Tag> : <Tag>停止</Tag>,
    },
    ...(canAdmin
      ? [
          {
            title: "",
            width: 180,
            render: (_: unknown, r: CatalogItem) => (
              <Space>
                <Button
                  size="small"
                  onClick={() =>
                    setItem({
                      id: r.id,
                      name: r.name,
                      fulfillment: r.fulfillment,
                      connector: r.connector,
                      default_owner: r.default_owner,
                      partner_id: r.partner_id,
                      active: r.active,
                      note: r.note,
                    })
                  }
                >
                  直す
                </Button>
                <Popconfirm
                  title={`「${r.name}」を消す`}
                  description="テンプレートや申請で使われていると消せない(停止にする)"
                  onConfirm={() =>
                    void del(() => api.deleteCatalogItem(r.id), "カタログ項目を消した")
                  }
                >
                  <Button size="small" danger>
                    削除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  const partnerColumns: ColumnsType<Partner> = [
    { title: "依頼先", width: 160, dataIndex: "name" },
    { title: "宛先", width: 240, dataIndex: "contact_email" },
    { title: "備考", width: 320, dataIndex: "note", ellipsis: true },
    {
      title: "状態",
      width: 110,
      render: (_: unknown, r: Partner) =>
        r.active ? <Tag color="green">有効</Tag> : <Tag>停止</Tag>,
    },
    ...(canAdmin
      ? [
          {
            title: "",
            width: 180,
            render: (_: unknown, r: Partner) => (
              <Space>
                <Button
                  size="small"
                  onClick={() =>
                    setPartner({
                      id: r.id,
                      name: r.name,
                      contact_email: r.contact_email,
                      active: r.active,
                      note: r.note,
                    })
                  }
                >
                  直す
                </Button>
                <Popconfirm
                  title={`「${r.name}」を消す`}
                  description="カタログ項目や工程から参照されていると消せない"
                  onConfirm={() => void del(() => api.deletePartner(r.id), "依頼先を消した")}
                >
                  <Button size="small" danger>
                    削除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  const fulfillment = item?.fulfillment ?? "auto";

  return (
    <div>
      <Typography.Title level={3}>カタログ</Typography.Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="カタログは SaaS のアカウント発行だけを並べる"
        description="端末準備や入館カードなどは WF設計の「工程」で定義する。自動(コネクタ)はコネクタ名が必須、社外発行は依頼先が必須。"
      />

      <Card
        size="small"
        title="SaaS 項目"
        style={{ marginBottom: 16 }}
        extra={
          canAdmin && (
            <Button
              type="primary"
              size="small"
              onClick={() => setItem({ fulfillment: "auto", active: true })}
            >
              項目を足す
            </Button>
          )
        }
      >
        <Table<CatalogItem>
          scroll={{ x: "max-content" }}
          rowKey="id"
          size="small"
          columns={itemColumns}
          dataSource={catalog.data?.items ?? []}
          loading={catalog.isFetching}
          pagination={false}
        />
      </Card>

      <Card
        size="small"
        title="依頼先(協力会社・派遣元)"
        extra={
          canAdmin && (
            <Button type="primary" size="small" onClick={() => setPartner({ active: true })}>
              依頼先を足す
            </Button>
          )
        }
      >
        <Table<Partner>
          scroll={{ x: "max-content" }}
          rowKey="id"
          size="small"
          columns={partnerColumns}
          dataSource={partners.data?.items ?? []}
          loading={partners.isFetching}
          pagination={false}
        />
      </Card>

      <Modal
        open={!!item}
        title={item?.id ? "カタログ項目を直す" : "カタログ項目を足す"}
        okText="保存"
        cancelText="やめる"
        confirmLoading={saveItem.isPending}
        onCancel={() => setItem(null)}
        onOk={() => saveItem.mutate()}
      >
        <Space direction="vertical" style={{ width: "100%", marginTop: 12 }}>
          <Input
            addonBefore="名前"
            value={item?.name ?? ""}
            maxLength={200}
            onChange={(e) => setItem((p) => ({ ...p!, name: e.target.value }))}
          />
          <Select
            style={{ width: "100%" }}
            value={fulfillment}
            onChange={(v: string) => setItem((p) => ({ ...p!, fulfillment: v }))}
            options={FULFILLMENTS}
          />
          {fulfillment === "auto" && (
            <Select
              style={{ width: "100%" }}
              placeholder="コネクタ(必須)"
              value={item?.connector || undefined}
              onChange={(v: string) => setItem((p) => ({ ...p!, connector: v }))}
              options={CONNECTORS.map((c) => ({ value: c, label: c }))}
            />
          )}
          {fulfillment === "manual" && (
            <Input
              addonBefore="担当"
              placeholder="情シス など"
              value={item?.default_owner ?? ""}
              onChange={(e) => setItem((p) => ({ ...p!, default_owner: e.target.value }))}
            />
          )}
          {fulfillment === "external" && (
            <Select
              style={{ width: "100%" }}
              placeholder="依頼先(必須)"
              value={item?.partner_id ?? undefined}
              onChange={(v: number) => setItem((p) => ({ ...p!, partner_id: v }))}
              options={(partners.data?.items ?? [])
                .filter((p) => p.active)
                .map((p) => ({ value: p.id, label: p.name }))}
            />
          )}
          <Input.TextArea
            rows={2}
            placeholder="備考"
            value={item?.note ?? ""}
            onChange={(e) => setItem((p) => ({ ...p!, note: e.target.value }))}
          />
          <Checkbox
            checked={item?.active ?? true}
            onChange={(e) => setItem((p) => ({ ...p!, active: e.target.checked }))}
          >
            有効(申請で選べる)
          </Checkbox>
        </Space>
      </Modal>

      <Modal
        open={!!partner}
        title={partner?.id ? "依頼先を直す" : "依頼先を足す"}
        okText="保存"
        cancelText="やめる"
        confirmLoading={savePartner.isPending}
        onCancel={() => setPartner(null)}
        onOk={() => savePartner.mutate()}
      >
        <Space direction="vertical" style={{ width: "100%", marginTop: 12 }}>
          <Input
            addonBefore="名前"
            value={partner?.name ?? ""}
            maxLength={200}
            onChange={(e) => setPartner((p) => ({ ...p!, name: e.target.value }))}
          />
          <Input
            addonBefore="宛先"
            placeholder="依頼メールの送り先"
            value={partner?.contact_email ?? ""}
            onChange={(e) => setPartner((p) => ({ ...p!, contact_email: e.target.value }))}
          />
          <Input.TextArea
            rows={2}
            placeholder="備考"
            value={partner?.note ?? ""}
            onChange={(e) => setPartner((p) => ({ ...p!, note: e.target.value }))}
          />
          <Checkbox
            checked={partner?.active ?? true}
            onChange={(e) => setPartner((p) => ({ ...p!, active: e.target.checked }))}
          >
            有効
          </Checkbox>
        </Space>
      </Modal>

      <Flex />
    </div>
  );
}
